"use client";

import { useMemo, useRef, useState } from "react";
import DOMPurify from "isomorphic-dompurify";
import { useWorkspace, WorkspaceFile } from "@/lib/workspace-context";
import { renderMarkdownHTML } from "./markdown-renderer";
import { WikiLinkPreview } from "./WikiLinkPreview";

interface MarkdownPreviewProps {
  file: WorkspaceFile;
  onOpen: (fileId: string) => void;
  onCreateWikiLink: (linkName: string) => void;
}

interface HoverPreviewState {
  linkName: string;
  rect: DOMRect;
}

/**
 * Vista de previsualización del contenido Markdown del archivo.
 * Renderiza con `renderMarkdownHTML` y sanitiza con DOMPurify.
 * Detecta clicks en wiki-links y abre hover-preview tras 500ms.
 */
export function MarkdownPreview({ file, onOpen, onCreateWikiLink }: MarkdownPreviewProps) {
  const { files } = useWorkspace();
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewState | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const safeRenderedHTML = useMemo(
    () => DOMPurify.sanitize(renderMarkdownHTML(file.content || "", files), {
      ALLOWED_TAGS: ["h1", "h2", "h3", "p", "strong", "em", "code", "pre", "blockquote", "ul", "ol", "li", "a", "div", "span", "input", "br"],
      ALLOWED_ATTR: ["href", "class", "data-wiki-link", "data-new-link", "type", "checked", "disabled", "target", "rel"],
    }),
    [file.content, files],
  );

  const handlePreviewClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const wikiLink = target.getAttribute("data-wiki-link");
    const isNew = target.getAttribute("data-new-link");

    if (wikiLink) {
      e.preventDefault();
      const cleanLink = wikiLink.replace(".md", "").toLowerCase().trim();
      const found = files.find(
        (f) =>
          f.type === "file" &&
          (f.name.replace(".md", "").toLowerCase().trim() === cleanLink ||
            f.name.toLowerCase().trim() === cleanLink),
      );

      if (found) {
        onOpen(found.id);
      } else if (isNew === "true") {
        onCreateWikiLink(wikiLink);
      }
    }
  };

  const schedulePreview = (target: HTMLAnchorElement, linkName: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setHoverPreview({ linkName, rect: target.getBoundingClientRect() });
    }, 500);
  };

  const cancelPreview = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setHoverPreview(null);
  };

  return (
    <>
      <div
        onClick={handlePreviewClick}
        dangerouslySetInnerHTML={{ __html: safeRenderedHTML }}
        className="prose prose-sm dark:prose-invert max-w-none text-[15px] leading-relaxed text-foreground/90 flex-1 select-text relative"
        onMouseOver={(e) => {
          const target = (e.target as HTMLElement).closest<HTMLAnchorElement>("a[data-wiki-link]");
          if (!target) return;
          const linkName = target.getAttribute("data-wiki-link");
          if (!linkName) return;
          schedulePreview(target, linkName.replace(".md", ""));
        }}
        onMouseOut={(e) => {
          const target = (e.target as HTMLElement).closest<HTMLAnchorElement>("a[data-wiki-link]");
          if (!target) return;
          cancelPreview();
        }}
      />
      <WikiLinkPreview
        preview={hoverPreview}
        files={files}
        onOpen={onOpen}
        onCreate={onCreateWikiLink}
        onClose={cancelPreview}
      />
    </>
  );
}
